import { Injectable } from 'angular-ts-decorators';

import { Domain } from '@shared/app/scripts/services/domain';
import type { IConfig } from '@shared/app/scripts/services/config';
import type { IStandardErrorParser } from '@shared/app/scripts/services/standard-error-parser';
import { omitBy } from 'lodash';

export class AccountAddressModel {
  public zipcode: string;
  public fax: string;
  public company: string;
  public type: string;
  public country: string;
  public houseno: string;
  public houseadd: string;
  public street: string;
  public city: string;
  public phone: string;
  public street2: string;
  public primary: boolean;

  constructor() {
    this.zipcode = '';
    this.fax = '';
    this.company = '';
    this.type = '';
    this.country = '';
    this.houseno = '';
    this.houseadd = '';
    this.street = '';
    this.city = '';
    this.phone = '';
    this.street2 = '';
    this.primary = false;
  }
}

export class AccountContactModel {
  public type: string;
  public primary: boolean;
  public name: string;
  public email: string;
  public phone: string;
  public mobile: string;

  constructor() {
    this.type = '';
    this.primary = true;
    this.name = '';
    this.email = '';
    this.phone = '';
    this.mobile = '';
  }
}

/**
 * Service in the coreModule. It takes care of communication with account
 * resource.
 */
@Injectable('account')
export class Account {
  private location: string;
  private readonly spynlUrl;
  constructor(
    private $http: ng.IHttpService,
    private $q: ng.IQService,
    private config: IConfig,
    private domain: Domain,
    private standardErrorParser: IStandardErrorParser,
  ) {
    'ngInject';

    this.spynlUrl =
      '//' + this.domain.sibling(this.config.spynlSubdomain) + '/account';
  }

  /**
   * Method that returns an empty address object.
   * @returns {Object} address object
   */
  getAddressModel(): AccountAddressModel {
    return new AccountAddressModel();
  }

  /**
   * Method that returns an empty contact object.
   * @returns {Object} contact object
   */
  contactModel(): AccountContactModel {
    return new AccountContactModel();
  }

  /**
   * Method that gets the account information from the backend.
   * @returns {httpPromise} promise based on the http request
   */
  get(): ng.IPromise<any> {
    return this.$http
      .get(`${this.spynlUrl}/get`)
      .catch(this.standardErrorParser)
      .then((response: any) => response.data.data)
      .catch((error) => this.$q.reject(error.data.message));
  }

  /**
   * Method that saves the given account details model to the backend.
   * @param {Object} accountDetails model you want to save
   * @returns {httpPromise} promise based on the http request
   */
  save(accountDetails: any): ng.IPromise<any> {
    // Remove empty values
    accountDetails = omitBy(accountDetails, (x) => x === '');
    return this.$http
      .post(`${this.spynlUrl}/save`, {
        data: accountDetails,
      })
      .catch(this.standardErrorParser)
      .then((response: any) => response.data.data)
      .catch((error) => this.$q.reject(error.data.message));
  }
}
